import { supabase } from "./supabase";

// O IO do Gerenciador de Estudos (000117) — o cronograma de metas do Aprovação
// Contábil.
//
// Este módulo transporta. Quem decide se o aluno pode ler é a RLS
// (`conteudo_liberado('gerenciador-de-estudos') or eh_admin()` no catálogo,
// `aluno_id = meu_profile_id()` no progresso); quem decide o que aparece é
// `GerenciadorDeEstudos.tsx`. Aqui não há regra de acesso nem de apresentação.
//
// ---------------------------------------------------------------------------
// O progresso vem do BANCO inteiro, nunca montado aqui
// ---------------------------------------------------------------------------
// `ac_meu_progresso()` devolve o numerador E o denominador porque o denominador
// SE MOVE: o cronograma cresceu cinco vezes (1..150 em ago/2024, a 77 em set, as
// 151-161 em out, as 162-208 em fev/2025 e as 209-275 em fev/2026). Quem
// concluiu tudo em fevereiro de 2025 via 100% e hoje veria 75,6% — a barra anda
// PARA TRÁS sem o aluno fazer nada.
//
// Por isso a tela mostra "12 de 275", que é honesto e não regride, e a
// porcentagem só como contexto. Somar isso em JavaScript criaria a segunda
// definição do mesmo número, que é o defeito de "agregar acesso em JS".
//
// ---------------------------------------------------------------------------
// "Retomar" é a MENOR meta não marcada, e isso não é detalhe
// ---------------------------------------------------------------------------
// O caminho óbvio é `última marcada + 1`. Medido no Bubble em 27/08/2026: só
// 680 dos 941 alunos com marcação (72,3%) formam um prefixo contíguo 1..N, e
// entre quem marcou de 50 a 99 metas apenas 25%. `max+1` pularia metas que 261
// alunos não fizeram. E os 72,3% são ilusão de amostra: 312 alunos marcaram
// exatamente UMA meta, e um conjunto de um elemento é sempre um prefixo.
//
// Quem decide isso é `ac_meu_progresso`, no banco.

export type Aula = {
  id: string;
  titulo: string;
  url: string | null;
  ordem: number;
  disciplina: string | null;
};

export type Caderno = {
  id: string;
  titulo: string;
  url: string;
  ordem: number;
};

export type Meta = {
  id: string;
  numero: number;
  relevancia: number | null;
  oq_aprender: string | null;
  dica: string | null;
  aulas: Aula[];
  cadernos: Caderno[];
};

/** A meta como ela aparece na LISTA — sem o conteúdo, que só a tela dela usa. */
export type MetaNaLista = {
  id: string;
  numero: number;
  relevancia: number | null;
  aulas: number;
  cadernos: number;
  /**
   * A meta tem ao menos uma aula com link que funciona.
   *
   * É a informação RARA e por isso a que vale dizer: 248 das 275 metas têm, e as
   * 27 que não têm (4 sem aula nenhuma — 272 a 275 — e 23 cujas aulas trazem "."
   * ou "x" no lugar do endereço) são justamente as que o aluno abriria achando
   * que a tela quebrou. O inverso — marcar "com orientação", que 179 metas têm —
   * seria repetir a mesma etiqueta em 65% da lista sem separar nada.
   */
  temLink: boolean;
  feita: boolean;
};

export type Progresso = {
  metas_feitas: number;
  metas_total: number;
  ultima_meta: number | null;
  proxima_meta: number | null;
  /** o tempo cronometrado, somando o Bubble e o hub por dia, com o teto de 24 h por dia (000363) */
  estudo_total_ms: number;
  dias_estudados: number;
};

/**
 * Uma imagem colada na anotação, no editor do Bubble (000363). A anotação do hub é texto simples, e
 * a imagem fica ABAIXO dela, na ordem do marcador "[imagem N]" do texto. `url` é nula enquanto o
 * arquivo não chegou ao bucket (a rodada diária copia) ou quando a origem não existe mais.
 */
export type ImagemDaAnotacao = { ordem: number; url: string | null };

export type Painel = {
  progresso: Progresso;
  metas: MetaNaLista[];
};

/**
 * Tudo que a tela de entrada precisa, numa ida só.
 *
 * As 275 metas com a contagem de aulas cabem numa consulta — não há paginação
 * porque o catálogo inteiro tem 275 linhas e a tela é um índice: lista densa de
 * texto encontra melhor que grade de cards, e é o uso real (procurar a meta pelo
 * número). É a lição do IR Pro, que saiu de 8.249px para 1.120px com o mesmo
 * conteúdo.
 */
export async function lerPainel(): Promise<Painel | null> {
  const [metas, feitas, progresso] = await Promise.all([
    supabase
      .from("ac_metas")
      .select(
        "id, numero, relevancia, oq_aprender, dica, " +
          "ac_aulas(id, url), ac_cadernos(id)",
      )
      .order("numero", { ascending: true }),
    supabase.from("ac_metas_feitas").select("meta_id"),
    supabase.rpc("ac_meu_progresso").single(),
  ]);

  if (metas.error || !metas.data) return null;

  // O cast é do `data` INTEIRO, como `lib/legaliza.ts` faz: o cliente do
  // Supabase não conhece o esquema deste projeto, e mapear antes de castar faz o
  // TypeScript ver `GenericStringError` em cada campo.
  type LinhaDoIndice = {
    id: string;
    numero: number;
    relevancia: number | null;
    oq_aprender: string | null;
    dica: string | null;
    ac_aulas: { id: string; url: string | null }[] | null;
    ac_cadernos: { id: string }[] | null;
  };

  const marcadas = new Set(
    ((feitas.data ?? []) as { meta_id: string }[]).map((f) => f.meta_id),
  );

  const lista: MetaNaLista[] = (metas.data as unknown as LinhaDoIndice[]).map((m) => ({
    id: m.id,
    numero: m.numero,
    relevancia: m.relevancia,
    aulas: (m.ac_aulas ?? []).length,
    cadernos: (m.ac_cadernos ?? []).length,
    temLink: (m.ac_aulas ?? []).some((a) => Boolean(a.url)),
    feita: marcadas.has(m.id),
  }));

  // O progresso vem da RPC. Se ela falhar (aluno sem perfil, portão fechado), a
  // tela ainda tem a lista — e o painel de fatos some, em vez de mostrar zero
  // grande. "Tile de fato só existe com dado medido."
  const p = (progresso.data ?? null) as Progresso | null;

  return {
    progresso: p ?? {
      metas_feitas: 0,
      metas_total: lista.length,
      ultima_meta: null,
      proxima_meta: lista[0]?.numero ?? null,
      estudo_total_ms: 0,
      dias_estudados: 0,
    },
    metas: lista,
  };
}

/**
 * Uma meta pelo NÚMERO — que é a identidade dela e o que está na rota.
 *
 * Devolve o `total` junto porque a tela diz "Meta 12 de 275": sem o total, o
 * aluno não sabe onde está no cronograma, e o total NÃO é constante — ele cresceu
 * cinco vezes, a última com 67 metas de uma vez em fevereiro de 2026.
 */
export async function lerMeta(
  numero: number,
): Promise<{
  meta: Meta;
  feita: boolean;
  anotacao: string | null;
  imagens: ImagemDaAnotacao[];
  total: number;
} | null> {
  const { data, error } = await supabase
    .from("ac_metas")
    .select(
      "id, numero, relevancia, oq_aprender, dica, " +
        "ac_aulas(id, titulo, url, ordem, ac_disciplinas(nome)), " +
        "ac_cadernos(id, titulo, url, ordem)",
    )
    .eq("numero", numero)
    .maybeSingle();

  if (error || !data) return null;

  type LinhaDaMeta = {
    id: string;
    numero: number;
    relevancia: number | null;
    oq_aprender: string | null;
    dica: string | null;
    ac_aulas:
      | {
          id: string;
          titulo: string;
          url: string | null;
          ordem: number;
          ac_disciplinas: { nome: string } | null;
        }[]
      | null;
    ac_cadernos: Caderno[] | null;
  };

  const linha = data as unknown as LinhaDaMeta;

  const [feita, anotacao, total] = await Promise.all([
    supabase.from("ac_metas_feitas").select("id").eq("meta_id", linha.id).maybeSingle(),
    supabase
      .from("ac_anotacoes")
      .select("texto, ac_anotacao_imagens(ordem, caminho)")
      .eq("meta_id", linha.id)
      .maybeSingle(),
    supabase.from("ac_metas").select("id", { count: "exact", head: true }),
  ]);
  const anot = anotacao.data as
    | { texto: string; ac_anotacao_imagens: { ordem: number; caminho: string | null }[] | null }
    | null;
  const imagens = await assinarImagensDaAnotacao(anot?.ac_anotacao_imagens ?? []);

  const meta: Meta = {
    id: linha.id,
    numero: linha.numero,
    relevancia: linha.relevancia,
    oq_aprender: linha.oq_aprender,
    dica: linha.dica,
    aulas: (linha.ac_aulas ?? [])
      .map((a) => ({
        id: a.id,
        titulo: a.titulo,
        url: a.url,
        ordem: a.ordem,
        disciplina: a.ac_disciplinas?.nome ?? null,
      }))
      .sort((x, y) => x.ordem - y.ordem),
    cadernos: [...(linha.ac_cadernos ?? [])].sort((x, y) => x.ordem - y.ordem),
  };

  return {
    meta,
    feita: Boolean(feita.data),
    anotacao: anot?.texto ?? null,
    imagens,
    total: total.count ?? 0,
  };
}

/**
 * O bucket `ac-anotacoes` é PRIVADO (a pasta é do aluno): as imagens saem por URL assinada, todas
 * num pedido só, com uma hora de validade, como na consultoria. A que falhar fica com `url` nula, e
 * a tela diz no lugar dela que não está disponível — a falha de uma imagem não derruba a meta.
 */
async function assinarImagensDaAnotacao(
  linhas: { ordem: number; caminho: string | null }[],
): Promise<ImagemDaAnotacao[]> {
  const ordenadas = [...linhas].sort((a, b) => a.ordem - b.ordem);
  const caminhos = ordenadas.flatMap((l) => (l.caminho ? [l.caminho] : []));
  let assinadas = new Map<string, string>();
  if (caminhos.length) {
    const { data, error } = await supabase.storage.from("ac-anotacoes").createSignedUrls(caminhos, 3600);
    if (!error && data) {
      assinadas = new Map(
        data.flatMap((d) => (d.path && d.signedUrl && !d.error ? [[d.path, d.signedUrl] as [string, string]] : [])),
      );
    }
  }
  return ordenadas.map((l) => ({ ordem: l.ordem, url: l.caminho ? assinadas.get(l.caminho) ?? null : null }));
}

// ---------------------------------------------------------------------------
// As escritas — todas por RPC, nenhuma recebe id de aluno
// ---------------------------------------------------------------------------
// `authenticated` não tem insert, update nem delete nas três tabelas de
// progresso: a tentativa volta 403. Cada função devolve a mensagem do banco ou
// `null` em caso de sucesso, como `lib/parametrosAdmin.ts` faz — zero regra
// aqui, porque as RPCs já validam.

export async function marcarMeta(metaId: string, feito: boolean): Promise<string | null> {
  const { error } = await supabase.rpc("ac_marcar_meta", { p_meta: metaId, p_feito: feito });
  return error?.message ?? null;
}

export async function salvarAnotacao(metaId: string, texto: string): Promise<string | null> {
  const { error } = await supabase.rpc("ac_salvar_anotacao", {
    p_meta: metaId,
    p_texto: texto,
  });
  return error?.message ?? null;
}

/**
 * Soma um trecho de cronômetro ao dia. Devolve o total do dia em ms (o do Bubble e o do hub
 * somados, com o teto de 24 h), ou a mensagem de erro.
 *
 * O cronômetro saiu em 27/08 (000119, Victor: "acho meio inútil") e VOLTOU em 29/10 (000363, Enzo),
 * com o histórico do Bubble. O código é o da 000117, devolvido do `git show f0ca446^`.
 *
 * A DATA é do navegador do aluno de propósito: é o dia que ele viu na tela. O Bubble fazia igual, e
 * é por isso que o campo `data` de lá tem quatro fusos gravados — reinterpretar tudo em
 * America/Sao_Paulo deslocaria 35 registros para o dia anterior, dando ao aluno uma barra num dia
 * em que ele não estudou.
 */
export async function registrarEstudo(ms: number): Promise<{ total_ms: number } | { erro: string }> {
  const hoje = new Date();
  const data = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(
    hoje.getDate(),
  ).padStart(2, "0")}`;
  const { data: total, error } = await supabase.rpc("ac_registrar_estudo", {
    p_data: data,
    p_ms: Math.round(ms),
  });
  if (error) return { erro: error.message };
  return { total_ms: Number(total ?? 0) };
}

// ---------------------------------------------------------------------------
// Apresentação de tempo — puro, e num lugar só
// ---------------------------------------------------------------------------

/** "2h 35min", "35min", "—" quando não há nada. Nunca milissegundos na tela. */
export function tempoLegivel(ms: number): string {
  if (!ms || ms < 60000) return "—";
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

/** O cronômetro na tela: 00:00:00. */
export function cronometroLegivel(ms: number): string {
  const s = Math.floor(ms / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}
